import { z } from 'zod';
import { stateKeySchema } from '../catalogue/state-rules.js';
import { type DocPrefix, parseDocNumber } from '../numbering/index.js';
import { nonNegativeQtyString, positiveQtyString } from '../quantity/index.js';
import { managerPinApprovalSchema } from '../stock-control/approvals.js';
import { WRITE_OFF_REASONS } from '../stock-control/writeoffs.js';
import { isPlainDate } from '../time/index.js';
import { defineCommand } from './registry.js';

/**
 * Stock-control station commands (M4, WP4.1): counts, write-offs, opening lots found on a
 * label day, and skipping a daily state task. Counts, write-offs and opening lots are
 * **physical** facts: never rejected for a business reason; above an approval threshold
 * without a valid manager-PIN approval they are applied **with flags** and an async
 * approval request (plan §9.3, D-100).
 * @module
 */

/** A document number with a given prefix, e.g. `SC-K7Q-00012` (plan §9.4). */
export function docNumberSchema(prefix: DocPrefix) {
  return z
    .string()
    .trim()
    .transform((s) => s.toUpperCase())
    .refine((s) => parseDocNumber(s)?.prefix === prefix, {
      message: `Expected a ${prefix}-<device>-<seq> number, e.g. ${prefix}-K7Q-00012`,
    });
}

const plainDate = z.string().refine(isPlainDate, 'Expected a date, YYYY-MM-DD');
const instant = z.iso.datetime({ offset: true });

/**
 * A new lot for stock found without a label (a count or a label day): the station makes
 * the id and the `L-<device>-<seq>` code (so it can print the label offline), and records
 * the best-before it reads or decides. Source `opening_count`.
 */
export const newLotSchema = z.object({
  lot_code: docNumberSchema('L'),
  /** Local calendar date; null for a product that doesn't track expiry. */
  best_before: plainDate.nullable(),
  produced_at: instant.optional(),
});

/** One counted row: a lot (or lot-less stock) of a product in one state. */
export const countLineSchema = z
  .object({
    product_id: z.uuid(),
    /** The lot; null for stock not tracked by lot. Set (device-made) with `new_lot`. */
    lot_id: z.uuid().nullable(),
    /** The state the stock is in (`default` for a product without a state rule). */
    state: stateKeySchema,
    counted_qty: nonNegativeQtyString,
    /** Unlabelled stock: create this lot (source `opening_count`) and count it. */
    new_lot: newLotSchema.optional(),
  })
  .refine((l) => l.new_lot === undefined || l.lot_id !== null, {
    message: 'A new lot needs its lot_id (made on the station)',
    path: ['lot_id'],
  });
/** One line of `count.submit`. */
export type CountLine = z.infer<typeof countLineSchema>;

/**
 * What a count covers. Every row of the scope with stock **as of `counted_at`** that has no
 * line is counted as **0** (not found): `location` = everything at the location;
 * `products` = those products at the location.
 */
export const countScopeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('location'), location_id: z.uuid() }),
  z.object({
    kind: z.literal('products'),
    location_id: z.uuid(),
    product_ids: z.array(z.uuid()).min(1).max(500),
  }),
]);
/** The scope of a count. */
export type CountScope = z.infer<typeof countScopeSchema>;

const lineKey = (l: CountLine) => `${l.product_id}|${l.lot_id ?? ''}|${l.state}`;

/**
 * `count.submit` (physical, `stock.count`): a stock count, a **snapshot at `counted_at`**
 * (plan §9.5). The server computes each row's system level as of `counted_at` (later
 * movements excluded, so a sale after the count is never corrected twice), writes the
 * count and one `count_adjustment` movement per row that differs (provenance `counted`).
 * `blind`: the station did not show system quantities, and the result carries none.
 * Adjustments above the threshold (D-100) need `approval` (manager PIN, permission
 * `stock.adjust`); without it they are applied with flag `needs_approval` and an async
 * approval request.
 */
export const countSubmit = defineCommand({
  type: 'count.submit',
  versions: { current: 1 },
  payload: z
    .object({
      /** Made on the station. */
      count_id: z.uuid(),
      number: docNumberSchema('SC'),
      scope: countScopeSchema,
      blind: z.boolean(),
      /** The snapshot instant (device clock). */
      counted_at: instant,
      /**
       * When the counting began (device clock), if the station knows. Movements at the
       * location between it and `counted_at` are flagged `moved_during_count` (the count
       * may have undone them).
       */
      started_at: instant.optional(),
      lines: z.array(countLineSchema).max(2000),
      approval: managerPinApprovalSchema.optional(),
      note: z.string().trim().max(500).optional(),
    })
    .superRefine((p, ctx) => {
      const seen = new Set<string>();
      p.lines.forEach((l, i) => {
        const key = lineKey(l);
        if (seen.has(key)) {
          ctx.addIssue({
            code: 'custom',
            message: 'The same lot and state is counted twice',
            path: ['lines', i],
          });
        }
        seen.add(key);
        if (p.scope.kind === 'products' && !p.scope.product_ids.includes(l.product_id)) {
          ctx.addIssue({
            code: 'custom',
            message: 'This product is not in the count scope',
            path: ['lines', i, 'product_id'],
          });
        }
      });
    }),
  physical: true,
  permission: 'stock.count',
});

/**
 * `writeoff.request` (physical, `writeoff.request`): stock is physically gone or binned.
 * Recorded at once as a `write_off` movement with a reason code (`other` needs a note).
 * Worth R250 or more (D-100) it needs `approval` (manager PIN, `writeoff.approve`); without
 * it, it is applied with flag `needs_approval` and an async approval request a manager can
 * confirm, or reject (a compensating movement puts the stock back on the books).
 * `state_task_id`: the daily-list task this write-off settles (it is marked done).
 */
export const writeOffRequest = defineCommand({
  type: 'writeoff.request',
  versions: { current: 1 },
  payload: z
    .object({
      /** Made on the station. */
      write_off_id: z.uuid(),
      number: docNumberSchema('WO'),
      product_id: z.uuid(),
      lot_id: z.uuid().nullable(),
      location_id: z.uuid(),
      state: stateKeySchema,
      qty: positiveQtyString,
      reason: z.enum(WRITE_OFF_REASONS),
      note: z.string().trim().max(500).optional(),
      state_task_id: z.uuid().optional(),
      approval: managerPinApprovalSchema.optional(),
    })
    .refine((p) => p.reason !== 'other' || (p.note ?? '') !== '', {
      message: 'Say why, when the reason is "other"',
      path: ['note'],
    }),
  physical: true,
  permission: 'writeoff.request',
});

/**
 * `lot.create_opening` (physical, `stock.count`): stock found without a label on a count
 * or a label day. Creates the lot (source `opening_count`, device-numbered `L-…` code,
 * best-before as read) and books the counted quantity as `opening` stock (provenance
 * `counted`) at the location and state. The station prints the label (and logs it with
 * `label.print_logged`) after this command, so the lot reaches the server first.
 */
export const lotCreateOpening = defineCommand({
  type: 'lot.create_opening',
  versions: { current: 1 },
  payload: z.object({
    /** Made on the station (the label's QR carries it). */
    lot_id: z.uuid(),
    product_id: z.uuid(),
    location_id: z.uuid(),
    state: stateKeySchema,
    qty: nonNegativeQtyString,
    lot_code: docNumberSchema('L'),
    best_before: plainDate.nullable(),
    produced_at: instant.optional(),
  }),
  physical: true,
  permission: 'stock.count',
});

/**
 * `state_task.skip` (non-physical, `stock.adjust`): a daily-list task that won't be done
 * (the stock isn't there, a person decided against a backward move…), with the reason.
 */
export const stateTaskSkip = defineCommand({
  type: 'state_task.skip',
  versions: { current: 1 },
  payload: z.object({
    state_task_id: z.uuid(),
    reason: z.string().trim().min(1).max(500),
  }),
  physical: false,
  permission: 'stock.adjust',
});

/** The stock-control commands (M4). */
export const STOCK_CONTROL_COMMANDS = [
  countSubmit,
  writeOffRequest,
  lotCreateOpening,
  stateTaskSkip,
] as const;
